import React, { useState } from "react";

import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { HelpBox, SearchBox } from "@/components/atoms";
import { AudienceMiniStats } from "@/components/molecules";
import { InsightFilters, InsightFiltersProvider } from "@/context";
import { AudienceSummary, ScoreType, TabOption } from "@/types";
import {
  insightLevelOptions,
  insightScoreOptions,
  insightSortOptions,
} from "@/utils";

type InsightLayoutProps = {
  tab?: TabOption;
  title?: string;
  summary?: AudienceSummary;
  children: React.ReactNode;
  onDownload?: (tab: TabOption) => void;
  extraSlot?: React.ReactElement;
};

const InsightLayout: React.FC<InsightLayoutProps> = ({
  tab,
  title,
  summary,
  children,
  onDownload,
  extraSlot
}) => {
  const [filters, setFilters] = useState<InsightFilters>({
    scoreType: { value: "score_wt", label: "Index" },
    levels: [1, 2, 3],
    searchText: "",
    sortType: "desc",
  });

  const anyFilters = tab?.showScores || tab?.showLevels || tab?.showSearch;

  const handleScoreToggle = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null
  ) => {
    const newScoreType: ScoreType | undefined = insightScoreOptions.find(
      (option) => option.value === value
    );
    value &&
      newScoreType &&
      setFilters((prev) => ({ ...prev, scoreType: newScoreType }));
  };

  const handleLevelToggle = (
    _event: React.MouseEvent<HTMLElement>,
    values: number[]
  ) => {
    setFilters((prev) => ({ ...prev, levels: values }));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFilters((prev) => ({ ...prev, searchText: event.target.value || "" }));

  const toLevelLabel = () => {
    if (filters.levels.length === 1) {
      return insightLevelOptions.filter((x) => x.value === filters.levels[0])[0]
        .label;
    } else if (filters.levels.length === 2) {
      return insightLevelOptions
        .filter((x) => filters.levels.includes(x.value))
        .map((x) => x.short)
        .join(" + ");
    } else {
      return "All";
    }
  };

  const toSortLabel = () => {
    return insightSortOptions.filter((x) => x.value === filters.sortType)[0]
      .label;
  };

  const handleSortTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue) {
      setFilters((prev) => ({ ...prev, sortType: newValue }));
    }
  };

  return (
    <InsightFiltersProvider filters={filters} setFilters={setFilters}>
      {/* Data Options are pinned to the right hand side */}
      {(anyFilters || title || summary) && (
        <Grid
          size={12}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography variant="h6">{title ?? tab?.label}</Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-end"
            justifyContent="flex-end"
          >
            {summary && (
              <AudienceMiniStats audienceSize={summary} size="small" />
            )}
            {tab?.showSearch && (
              <HelpBox title="Use this search to filter the items below. You may need to enter at least 3 characters for the search to be applied">
                <Box display="flex" alignItems="flex-end">
                  <SearchBox value={filters.searchText} onSearch={handleSearch} />
                </Box>
              </HelpBox>
            )}
            {tab?.showScores && (
              <Stack>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  mr={0.5}
                >
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    Score:
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    {filters.scoreType.label}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={filters.scoreType.value}
                  onChange={handleScoreToggle}
                >
                  {insightScoreOptions.map((option, index) => (
                    <HelpBox
                      key={`score-toggle-${index}`}
                      title={option.description}
                    >
                      <ToggleButton value={option.value}>
                        {option.icon}
                      </ToggleButton>
                    </HelpBox>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            {tab?.showLevels && (
              <Stack>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  mr={0.5}
                >
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    Level:
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    {toLevelLabel()}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  value={filters.levels}
                  onChange={handleLevelToggle}
                >
                  {insightLevelOptions.map((option, index) => (
                    <HelpBox
                      key={`level-toggle-${index}`}
                      title={option.description}
                    >
                      <ToggleButton value={option.value}>
                        {option.icon}
                      </ToggleButton>
                    </HelpBox>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            {tab?.showSorts && (
              <Stack>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  mr={0.5}
                >
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    Sort:
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    {toSortLabel()}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={filters.sortType}
                  onChange={handleSortTypeChange}
                  exclusive
                  size="small"
                  aria-label="sort direction"
                >
                  {insightSortOptions.map((option, index) => (
                    <HelpBox
                      key={`sort-toggle-${index}`}
                      title={option.description}
                    >
                      <ToggleButton value={option.value}>
                        {option.icon}
                      </ToggleButton>
                    </HelpBox>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            {tab?.showDownload && onDownload && (
              <HelpBox title={`Download ${tab.label} Data`}>
                <IconButton
                  sx={{
                    border: "1px solid",
                    borderRadius: "4px",
                    borderColor: "divider",
                    height: 40,
                    width: 40,
                  }}
                  color="default"
                  onClick={() => onDownload(tab)}
                >
                  <Download fontSize="medium" color="inherit" />
                </IconButton>
              </HelpBox>
            )}
            {extraSlot}
          </Stack>
        </Grid>
      )}

      {/* Content */}
      {children}
    </InsightFiltersProvider>
  );
};

export default InsightLayout;
